import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, plain, inputClass, selectClass, channelOptions, relative, duration, asList } from './dashboardShared';

// The dashboard's Feeds tab: Whatnot, Instagram, eBay, YouTube, Twitch, Reddit and RSS. Adding and removing run the cogs' own commands (or,
// for Whatnot, the functions its Discord control panel uses), so they check things the same way as in Discord.

// Whatnot, Instagram and eBay are the site's own full-colour icons. The rest are drawn, in white on their selected button
// and their brand colour otherwise
const picture = (src) => <img src={src} alt="" className="w-4 h-4 object-contain" />;
const drawn = (path) => <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5"><path d={path} /></svg>;

const FEEDS = [
  // Whatnot sellers share their alert channels, set at the top of its tab
  { id: 'whatnot', label: 'Whatnot', color: '#fbe510', text: '#000', icon: picture('/wnlogo.png'), placeholder: 'Whatnot username or profile link' },
  { id: 'instagram', label: 'Instagram', color: '#e1306c', icon: picture('/instagram.png'), placeholder: 'Instagram username or profile link' },
  { id: 'ebay', label: 'eBay', color: '#86b817', icon: picture('/ebay.png'), placeholder: 'eBay username, not their shop name' },
  { id: 'youtube', label: 'YouTube', color: '#ff0000', placeholder: 'Channel link or @handle',
    icon: drawn('M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8ZM9.6 15.6V8.4l6.2 3.6-6.2 3.6Z') },
  { id: 'twitch', label: 'Twitch', color: '#9146ff', placeholder: 'Twitch username, e.g. togecheeks',
    icon: drawn('M4.3 3 3 6.4v13.2h4.6V22h2.6l2.4-2.4h3.7l5-5V3H4.3Zm15.4 10.8-2.9 2.9h-4.6l-2.4 2.4v-2.4H5.9V4.7h13.8v9.1ZM16.8 7.8v5h-1.7v-5h1.7Zm-4.6 0v5h-1.7v-5h1.7Z') },
  { id: 'reddit', label: 'Reddit', color: '#ff4500', placeholder: 'Subreddit, e.g. PokemonTCG',
    icon: drawn('M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z') },
  // Any RSS or Atom feed: a short name of your choosing, and its link (rssLink)
  { id: 'rss', label: 'RSS', color: '#f78422', placeholder: 'Short name, e.g. pokebeach', rssLink: true,
    icon: drawn('M19.199 24C19.199 13.467 10.533 4.8 0 4.8V0c13.165 0 24 10.835 24 24h-4.801zM3.291 17.415c1.814 0 3.293 1.479 3.293 3.295 0 1.813-1.485 3.29-3.301 3.29C1.47 24 0 22.526 0 20.71s1.475-3.294 3.291-3.295zM15.909 24h-4.665c0-6.169-5.075-11.245-11.244-11.245V8.09c8.727 0 15.909 7.184 15.909 15.91z') },
];

// An RSS feed's settings, as ?rss set and ?rss filter change them: opened from its row's Edit button. Only what's
// been changed is sent, one setting at a time, so each is checked by the cog the same way as in Discord.
const RSS_CHOICES = { embed: ['on', 'off'], image: ['auto', 'page', 'feed', 'off'], updates: ['auto', 'on', 'off'] };

function RssEditor({ ask, name, failed, setNote, onClose }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    ask('rss.get', { name }).then((answer) => {
      if (failed(answer)) { onClose(); return; }
      setData(answer.data);
      setForm({ ...answer.data.settings, include: answer.data.include, exclude: answer.data.exclude });
    });
  }, [name]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return <p className="text-zinc-500 text-xs py-2">Loading its settings…</p>;
  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const changed = Object.keys(form).filter(key => form[key] !== (key in data.settings ? data.settings[key] : data[key]));

  const save = async () => {
    setBusy('save'); setNote(null);
    const said = [];
    let ok = true;
    for (const key of changed) {
      const answer = ['include', 'exclude'].includes(key)
        ? await ask('rss.filter', { name, kind: key, words: form[key] })
        : await ask('rss.set', { name, setting: key, value: form[key] });
      if (failed(answer)) { setBusy(''); return; }
      said.push(answer.data.message.split('\n')[0]);
      ok = ok && answer.data.ok;
    }
    setBusy('');
    setNote({ text: said.join('\n') || 'Nothing changed.', ok });
    if (ok) {
      setData(d => ({ ...d, settings: { ...d.settings, ...Object.fromEntries(changed.filter(k => k in d.settings).map(k => [k, form[k]])) },
        include: form.include, exclude: form.exclude }));
    }
  };

  const postNow = async () => {
    setBusy('post'); setNote(null);
    const answer = await ask('rss.force', { name, count: 1 }, 60000);
    setBusy('');
    if (failed(answer)) return;
    setNote({ text: answer.data.message, ok: answer.data.ok });
  };

  const field = (key) => (
    <label key={key} className="block">
      <span className="block text-zinc-300 text-[11px] font-semibold mb-1 capitalize">{key}</span>
      {RSS_CHOICES[key] ? (
        <select value={form[key]} onChange={e => set(key, e.target.value)} className={`w-full ${selectClass}`}>
          {RSS_CHOICES[key].map(choice => <option key={choice} value={choice}>{choice}</option>)}
        </select>
      ) : key === 'colour' ? (
        <span className="flex gap-1.5">
          <input type="color" value={/^#[0-9a-f]{6}$/i.test(form.colour) ? form.colour : '#04aff7'} onChange={e => set('colour', e.target.value.toUpperCase())}
            className="h-[30px] w-10 rounded-md bg-zinc-950 border border-zinc-700 p-0.5 flex-shrink-0" />
          <input value={form.colour} onChange={e => set('colour', e.target.value)} className={inputClass} />
        </span>
      ) : (
        <input value={form[key]} onChange={e => set(key, e.target.value)} placeholder="(empty)" className={inputClass} />
      )}
      {data.help[key] && <span className="block text-zinc-500 text-[10px] mt-1 leading-snug">{data.help[key]}</span>}
    </label>
  );

  return (
    <div className="mt-2 mb-1 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 space-y-3">
      <p className="text-zinc-500 text-[10px] truncate">{data.url}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {['message', 'title', 'description', 'footer', 'button', 'colour', 'embed', 'image', 'updates'].map(field)}
      </div>
      <p className="text-zinc-500 text-[10px]">Text can use tags like {'{title}'}, {'{link}'} and {'{summary:200}'}: ?rss tags {data.name} lists them all.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {['include', 'exclude'].map(key => (
          <label key={key} className="block">
            <span className="block text-zinc-300 text-[11px] font-semibold mb-1">{key === 'include' ? 'Only post items with' : "Don't post items with"}</span>
            <input value={form[key]} onChange={e => set(key, e.target.value)} placeholder="words, comma separated" className={inputClass} />
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5 justify-end">
        <Button small tone="ghost" onClick={onClose} disabled={!!busy}>Close</Button>
        <Button small onClick={postNow} disabled={!!busy}>{busy === 'post' ? 'Posting…' : 'Post newest now'}</Button>
        <Button small tone="primary" onClick={save} disabled={!!busy || !changed.length}>{busy === 'save' ? 'Saving…' : `Save${changed.length ? ` (${changed.length})` : ''}`}</Button>
      </div>
    </div>
  );
}

export default function FeedsTab({ ask, lists, setNote, failed }) {
  const [kind, setKind] = useState(FEEDS[0].id);
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState(null); // Whatnot's shared channels: { alerts, listings, logs }
  const [status, setStatus] = useState(null);     // Whatnot's check status
  const [notice, setNotice] = useState(null);     // a problem with the whole feed, e.g. Instagram being paused
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');       // an RSS feed's link
  const [newChannel, setNewChannel] = useState('');
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState('');
  const [editing, setEditing] = useState('');     // the RSS feed whose settings are open

  const load = useCallback(async (which) => {
    setItems(null); setNotice(null);
    const answer = await ask('feeds.list', { kind: which });
    if (answer.ok) {
      setItems(asList(answer.data?.items)); // Firebase drops empty lists
      setNotice(answer.data?.notice || null);
      setSettings(answer.data?.settings || (which === 'whatnot' ? {} : null));
      setStatus(answer.data?.status ? { text: '', everyMinutes: 5, ...answer.data.status } : null);
    } else { setItems([]); failed(answer); }
  }, [ask]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(kind); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const feed = FEEDS.find(f => f.id === kind);
  const isWhatnot = kind === 'whatnot';
  const channelName = (id) => lists.channels.find(c => c.id === id)?.name;
  const roleName = (id) => lists.roles.find(r => r.id === id)?.name;

  const add = async () => {
    setBusy('add'); setNote(null);
    const answer = await ask('feeds.add', { kind, name: newName, url: newUrl, channel: newChannel });
    setBusy('');
    if (failed(answer)) return;
    setNote({ text: plain(answer.data.message, lists.channels, lists.roles), ok: answer.data.ok });
    if (answer.data.ok) { setNewName(''); setNewUrl(''); load(kind); }
  };

  const remove = async (item) => {
    if (confirm !== item.id) { setConfirm(item.id); return; }
    setConfirm(''); setBusy(`remove-${item.id}`); setNote(null);
    const answer = await ask('feeds.remove', { kind, name: kind === 'youtube' ? item.url : item.id });
    setBusy('');
    if (failed(answer)) return;
    setNote({ text: plain(answer.data.message, lists.channels, lists.roles), ok: answer.data.ok });
    load(kind);
  };

  const setWhatnotChannel = async (which, channel) => {
    setBusy(which); setNote(null);
    const answer = await ask('whatnot.channels', { [which]: channel });
    setBusy('');
    if (failed(answer)) return;
    setNote({ text: answer.data.message, ok: true });
    setSettings(s => ({ ...(s || {}), [which]: channel || null }));
  };

  const checkNow = async (shops) => {
    setBusy(shops ? 'shopcheck' : 'check'); setNote(null);
    const answer = await ask(shops ? 'whatnot.shopcheck' : 'whatnot.check', {}, 120000); // a full check can take a while
    setBusy('');
    if (failed(answer)) return;
    setNote({ text: answer.data.message, ok: true });
    load('whatnot');
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {FEEDS.map(f => (
          <button key={f.id} onClick={() => { setKind(f.id); setNote(null); setConfirm(''); setEditing(''); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors"
            style={kind === f.id ? { background: f.color, borderColor: f.color, color: f.text || 'white' } : { borderColor: '#3f3f46', color: '#a1a1aa' }}>
            <span style={kind === f.id ? undefined : { color: f.color }}>{f.icon}</span>
            {f.label}
          </button>
        ))}
      </div>

      {isWhatnot && (
        <Card title="Checks" right={
          <div className="flex gap-1.5">
            <Button small onClick={() => checkNow(false)} disabled={!!busy}>{busy === 'check' ? 'Checking…' : '🔄 Check now'}</Button>
            <Button small onClick={() => checkNow(true)} disabled={!!busy}>{busy === 'shopcheck' ? 'Checking…' : '🛍️ Check shops'}</Button>
          </div>
        }>
          {!status ? <p className="text-zinc-500 text-xs">Loading…</p> : (
            <div className="flex items-start gap-2">
              <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${status.ok ? 'bg-[#23a55a]' : 'bg-amber-400'}`} />
              <div className="min-w-0">
                <p className="text-white text-xs font-semibold">{status.text.replace(/^[✅⚠️⏳]️?\s*/u, '')}</p>
                <p className="text-zinc-500 text-[11px]">
                  {status.lastOk ? `Live status checked ${relative(status.lastOk)}` : 'Not checked yet'}
                  {` · automatically every ${status.everyMinutes} minutes`}
                  {status.lastShop ? ` · shops checked ${relative(status.lastShop)}` : ''}
                </p>
                {!status.ok && <p className="text-zinc-600 text-[10px] mt-0.5">Whatnot said: {status.detail}</p>}
              </div>
            </div>
          )}
        </Card>
      )}

      {isWhatnot && (
        <Card title="Where Whatnot posts go (shared by every seller)">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[['alerts', '🔴 Live alerts', null], ['listings', '🛍️ New listing alerts', 'Off'], ['logs', '⚠️ Error reports', 'Off']].map(([which, label, off]) => (
              <label key={which} className="block">
                <span className="block text-zinc-400 text-[11px] mb-1">{label}</span>
                <select value={settings?.[which] || ''} disabled={!settings || !!busy}
                  onChange={e => (e.target.value || off) && setWhatnotChannel(which, e.target.value)} className={`w-full ${selectClass}`}>
                  {off ? <option value="">{off}</option> : <option value="" disabled>Pick a channel</option>}
                  {channelOptions(lists.channels)}
                </select>
              </label>
            ))}
          </div>
        </Card>
      )}

      <Card title={isWhatnot ? 'Add a seller' : `Follow on ${feed.label}`}>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder={feed.placeholder}
            onKeyDown={e => { if (e.key === 'Enter' && newName && (isWhatnot || newChannel) && !busy) add(); }}
            className={`flex-1 ${inputClass}`} />
          {feed.rssLink && (
            <input value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="Feed or website link"
              className={`flex-[2] ${inputClass}`} />
          )}
          {!isWhatnot && (
            <select value={newChannel} onChange={e => setNewChannel(e.target.value)} className={`sm:w-56 ${selectClass}`}>
              <option value="">Post in which channel?</option>
              {channelOptions(lists.channels)}
            </select>
          )}
          <Button tone="primary" onClick={add} disabled={!newName || (!isWhatnot && !newChannel) || (feed.rssLink && !newUrl) || !!busy}>{busy === 'add' ? 'Adding…' : 'Add'}</Button>
        </div>
      </Card>

      <Card title={items ? `${isWhatnot ? 'Sellers' : 'Following'} · ${items.length}${isWhatnot ? ` · ${items.filter(i => i.live).length} live` : ''}` : 'Following'}>
        {items === null ? <p className="text-zinc-500 text-xs">Loading…</p>
          : items.length === 0 ? <p className="text-zinc-500 text-xs">{isWhatnot ? 'No sellers on the list yet.' : `Nothing followed on ${feed.label} yet.`}</p> : (
            <ul className="divide-y divide-zinc-800">
              {items.map(item => (
                <li key={item.id} className="py-2">
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    {/* Only the name itself is the link: clicking elsewhere in the row does nothing */}
                    <p className="text-white text-xs font-semibold truncate">
                      <a href={item.url} target="_blank" rel="noreferrer" className="hover:underline">{item.name}</a>
                      {item.live && <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-600 text-white align-middle">LIVE{item.viewers != null ? ` · ${item.viewers.toLocaleString()} watching` : ''}</span>}
                    </p>
                    {isWhatnot && item.show && (
                      <p className="text-zinc-300 text-[11px] truncate">
                        {item.show.url ? <a href={item.show.url} target="_blank" rel="noreferrer" className="hover:underline">{item.show.title || 'Live show'}</a> : (item.show.title || 'Live show')}
                        <span className="text-zinc-500">{` · peak ${(item.show.peak || 0).toLocaleString()}`}{item.show.startedAt ? ` · live ${duration(Date.now() - item.show.startedAt)}` : ''}</span>
                      </p>
                    )}
                    {isWhatnot && !item.live && (
                      <p className="text-zinc-500 text-[11px] truncate">
                        {item.last ? `Last stream ${item.last.endedAt ? relative(item.last.endedAt) : ''}: ${item.last.title || 'untitled'} · peak ${(item.last.peak || 0).toLocaleString()}`
                          : 'No streams seen yet'}
                      </p>
                    )}
                    {!isWhatnot && (
                      <p className="text-zinc-500 text-[11px] truncate">
                        {item.channel ? `→ #${channelName(item.channel) || 'deleted channel'}` : '→ no channel'}
                        {item.role ? ` · pings @${roleName(item.role) || 'deleted role'}` : ''}
                        {item.note ? ` · ${item.note}` : ''}
                      </p>
                    )}
                    {item.problem && <p className="text-amber-400 text-[11px]">{plain(item.problem, lists.channels, lists.roles)}</p>}
                  </div>
                  {kind === 'rss' && (
                    <Button small onClick={() => setEditing(editing === item.id ? '' : item.id)} disabled={!!busy}>
                      {editing === item.id ? 'Close' : 'Edit'}
                    </Button>
                  )}
                  <Button small tone={confirm === item.id ? 'danger' : 'ghost'} onClick={() => remove(item)} disabled={!!busy}>
                    {busy === `remove-${item.id}` ? '…' : confirm === item.id ? 'Sure?' : 'Remove'}
                  </Button>
                </div>
                {kind === 'rss' && editing === item.id && (
                  <RssEditor ask={ask} name={item.id} failed={failed} setNote={setNote} onClose={() => setEditing('')} />
                )}
                </li>
              ))}
            </ul>
          )}
        {(kind === 'twitch' || kind === 'youtube') && (
          <p className="text-zinc-600 text-[10px] mt-3">Role pings are set in Discord for now: ?{kind} ping &lt;name&gt; @role</p>
        )}
        {kind === 'ebay' && (
          <p className="text-zinc-600 text-[10px] mt-3">Finds listings with “pokemon” in the title. For another word: ?ebayfeed add &lt;seller&gt; #channel &lt;word&gt;</p>
        )}
        {notice && <p className="text-amber-400 text-[11px] mt-3">{notice}</p>}
        {isWhatnot && settings && !settings.alerts && (
          <p className="text-amber-400 text-[11px] mt-3">Pick a live alerts channel above, or no live alerts will be posted.</p>
        )}
      </Card>
    </div>
  );
}
